import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Modal,
  Spinner,
  Alert,
  Row,
  Col,
  Container,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createOrUpdateContactInfo,
  getContactInfo,
  deleteContactInfo,
  ContactInfo,
} from "api/apis";

const PersonalInfo: React.FC = () => {
  document.title = "Contact Information";

  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Form state
  const [formData, setFormData] = useState<ContactInfo>({
    officeNo: "",
    floor: "",
    building: "",
    landmark: "",
    street: "",
    area: "",
    pincode: "",
    email: "",
    phone: "",
    mapLink: "",
    mapIframe: "",
  });

  // Fetch contact info on component mount
  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async (): Promise<void> => {
    try {
      setLoading(true);
      const contactData = await getContactInfo();

      console.log("Fetched contact data:", contactData);

      if (contactData) {
        setContactInfo(contactData);
        setFormData(contactData);
      } else {
        setContactInfo(null);
        setFormData({
          officeNo: "",
          floor: "",
          building: "",
          landmark: "",
          street: "",
          area: "",
          pincode: "",
          email: "",
          phone: "",
          mapLink: "",
          mapIframe: "",
        });
      }
    } catch (error: any) {
      console.error("Error fetching contact info:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch contact information");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const requiredFields: (keyof ContactInfo)[] = [
      "officeNo",
      "floor",
      "building",
      "street",
      "area",
      "pincode",
      "email",
      "phone",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]?.trim()) {
        newErrors[field] = `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required`;
      }
    });

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.phone &&
      !/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    try {
      setSaving(true);
      const response = await createOrUpdateContactInfo(formData);

      console.log("Saved contact data:", response);

      setContactInfo(response);
      setIsEditing(false);
      toast.success("Contact information saved successfully!");
    } catch (error: any) {
      console.error("Error saving contact info:", error);
      toast.error(
        error.response?.data?.error || "Failed to save contact information"
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (): Promise<void> => {
    if (!contactInfo?._id) return;

    try {
      await deleteContactInfo(contactInfo._id);
      setContactInfo(null);
      setFormData({
        officeNo: "",
        floor: "",
        building: "",
        landmark: "",
        street: "",
        area: "",
        pincode: "",
        email: "",
        phone: "",
        mapLink: "",
        mapIframe: "",
      });
      setShowDeleteModal(false);
      toast.success("Contact information deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting contact info:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete contact information"
      );
    }
  };

  // Start editing
  const startEditing = (): void => {
    setIsEditing(true);
  };

  // Cancel editing
  const cancelEditing = (): void => {
    if (contactInfo) {
      setFormData(contactInfo);
    }
    setIsEditing(false);
    setErrors({});
  };

  if (loading) {
    return (
      <div
        style={{
          marginTop: "6rem",
          padding: "2rem",
          marginLeft: "250px",
          minHeight: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div style={{ textAlign: "center", padding: "3rem 0" }}>
          <Spinner animation="border" variant="primary" />
          <p style={{ marginTop: "1rem" }}>Loading contact information...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: "6rem",
        padding: "2rem",
        // marginLeft: "250px",
        minHeight: "100vh",
        // backgroundColor: "#f8f9fa",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", margin: "0 -15px" }}>
        <div style={{ flex: "0 0 100%", maxWidth: "100%", padding: "0 15px" }}>
          <Card
            style={{
              border: "none",
              borderRadius: "12px",
              boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
            }}
          >
            <Card.Header
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                // backgroundColor: "white",
                padding: "1rem",
                borderBottom: "2px solid #e9ecef",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h4 style={{ margin: 0, color: "#0d6efd" }}>
                Contact Information
              </h4>
              <div>
                {!isEditing ? (
                  <Button
                    variant="primary"
                    onClick={startEditing}
                    style={{ padding: "0.375rem 1.5rem" }}
                  >
                    {contactInfo ? "Edit" : "Add"} Contact Information
                  </Button>
                ) : (
                  <Button variant="outline-secondary" onClick={cancelEditing}>
                    Cancel
                  </Button>
                )}
              </div>
            </Card.Header>

            <Card.Body style={{ padding: "2rem" }}>
              {!isEditing ? (
                // Display mode
                contactInfo ? (
                  <Row>
                    <Col md={6} style={{ marginBottom: "2rem" }}>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "0.375rem",
                          padding: "1.5rem",
                          height: "100%",
                        }}
                      >
                        <h5 style={{ color: "#0d6efd", marginBottom: "1rem" }}>
                          Office Address
                        </h5>
                        <div>
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Office No:</strong> {contactInfo.officeNo}
                          </p>
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Floor:</strong> {contactInfo.floor}
                          </p>
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Building:</strong> {contactInfo.building}
                          </p>
                          {contactInfo.landmark && (
                            <p
                              style={{
                                marginBottom: "0.5rem",
                                lineHeight: "1.4",
                              }}
                            >
                              <strong>Landmark:</strong> {contactInfo.landmark}
                            </p>
                          )}
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Street:</strong> {contactInfo.street}
                          </p>
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Area:</strong> {contactInfo.area}
                          </p>
                          <p style={{ marginBottom: 0, lineHeight: "1.4" }}>
                            <strong>Pincode:</strong> {contactInfo.pincode}
                          </p>
                        </div>

                        {contactInfo.fullAddress && (
                          <div
                            style={{
                              marginTop: "1.5rem",
                              paddingTop: "1rem",
                              borderTop: "1px solid #dee2e6",
                            }}
                          >
                            <h6 style={{ color: "#0d6efd" }}>Full Address</h6>
                            <p
                              style={{ color: "#6c757d", fontSize: "0.875rem" }}
                            >
                              {contactInfo.fullAddress}
                            </p>
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "0.375rem",
                          padding: "1.5rem",
                          height: "100%",
                        }}
                      >
                        <h5 style={{ color: "#0d6efd", marginBottom: "1rem" }}>
                          Contact Details
                        </h5>
                        <div style={{ marginBottom: "1.5rem" }}>
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Email:</strong>
                            <a
                              href={`mailto:${contactInfo.email}`}
                              style={{ marginLeft: "0.25rem" }}
                            >
                              {contactInfo.email}
                            </a>
                          </p>
                          <p
                            style={{
                              marginBottom: "0.5rem",
                              lineHeight: "1.4",
                            }}
                          >
                            <strong>Phone:</strong>
                            <a
                              href={`tel:${contactInfo.phone}`}
                              style={{ marginLeft: "0.25rem" }}
                            >
                              {contactInfo.phone}
                            </a>
                          </p>
                          {contactInfo.mapLink && (
                            <p style={{ marginBottom: 0, lineHeight: "1.4" }}>
                              <strong>Map Link:</strong>
                              <br />
                              <a
                                href={contactInfo.mapLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                View on Google Maps
                              </a>
                            </p>
                          )}
                        </div>

                        {contactInfo.mapIframe && (
                          <div style={{ marginTop: "1rem" }}>
                            <h6
                              style={{
                                color: "#0d6efd",
                                marginBottom: "0.5rem",
                              }}
                            >
                              Location Map
                            </h6>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: contactInfo.mapIframe,
                              }}
                              style={{
                                maxWidth: "100%",
                                height: "200px",
                                borderRadius: "8px",
                                overflow: "hidden",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </Col>

                    {contactInfo.createdAt && (
                      <Col md={12} style={{ marginTop: "1rem" }}>
                        <div style={{ textAlign: "center" }}>
                          <small style={{ color: "#6c757d" }}>
                            Last updated:{" "}
                            {new Date(
                              contactInfo.updatedAt || contactInfo.createdAt
                            ).toLocaleDateString("en-GB")}
                          </small>
                        </div>
                      </Col>
                    )}
                  </Row>
                ) : (
                  <Alert
                    variant="info"
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <i className="fas fa-info-circle me-2"></i>
                    No contact information found. Click "Add Contact
                    Information" to create one.
                  </Alert>
                )
              ) : (
                // Edit mode
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={6}>
                      <h5 style={{ color: "#0d6efd", marginBottom: "1rem" }}>
                        Office Address
                      </h5>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Office No. *</Form.Label>
                        <Form.Control
                          type="text"
                          name="officeNo"
                          value={formData.officeNo}
                          onChange={handleInputChange}
                          placeholder="e.g., Off 13/A"
                          isInvalid={!!errors.officeNo}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.officeNo}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Floor *</Form.Label>
                        <Form.Control
                          type="text"
                          name="floor"
                          value={formData.floor}
                          onChange={handleInputChange}
                          placeholder="e.g., 4th Floor"
                          isInvalid={!!errors.floor}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.floor}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Building *</Form.Label>
                        <Form.Control
                          type="text"
                          name="building"
                          value={formData.building}
                          onChange={handleInputChange}
                          placeholder="e.g., 97/101"
                          isInvalid={!!errors.building}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.building}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Landmark</Form.Label>
                        <Form.Control
                          type="text"
                          name="landmark"
                          value={formData.landmark}
                          onChange={handleInputChange}
                          placeholder="e.g., Near Mumbadevi Chambers"
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={6}>
                      <h5 style={{ color: "#0d6efd", marginBottom: "1rem" }}>
                        Address Details
                      </h5>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Street *</Form.Label>
                        <Form.Control
                          type="text"
                          name="street"
                          value={formData.street}
                          onChange={handleInputChange}
                          placeholder="e.g., Shaikh Memon Street"
                          isInvalid={!!errors.street}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.street}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Area *</Form.Label>
                        <Form.Control
                          type="text"
                          name="area"
                          value={formData.area}
                          onChange={handleInputChange}
                          placeholder="e.g., Zaveri Bazar-Kalbadevi"
                          isInvalid={!!errors.area}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.area}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Pincode *</Form.Label>
                        <Form.Control
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          placeholder="e.g., 400002"
                          maxLength={6}
                          isInvalid={!!errors.pincode}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.pincode}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Email *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="e.g., anjalgold@gmail.com"
                          isInvalid={!!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Phone *</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="e.g., 07942694079"
                          isInvalid={!!errors.phone}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phone}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Google Maps Link</Form.Label>
                        <Form.Control
                          type="url"
                          name="mapLink"
                          value={formData.mapLink}
                          onChange={handleInputChange}
                          placeholder="e.g., https://maps.google.com/?q=19.243,72.845"
                        />
                        <Form.Text style={{ color: "#6c757d" }}>
                          Paste the full Google Maps URL for your location
                        </Form.Text>
                      </Form.Group>
                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Google iFrame Link</Form.Label>
                        <Form.Control
                          type="text"
                          name="mapIframe"
                          value={formData.mapIframe}
                          onChange={handleInputChange}
                          placeholder="e.g., <iframe src=https://www.google.com/maps/embed?pb=!1m18!1m12></iframe>"
                        />
                        <Form.Text style={{ color: "#6c757d" }}>
                          Paste the full Google Maps URL for your location
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "1.5rem",
                      paddingTop: "1rem",
                      borderTop: "1px solid #dee2e6",
                    }}
                  >
                    <div>
                      {contactInfo && (
                        <Button
                          variant="outline-danger"
                          onClick={() => setShowDeleteModal(true)}
                          type="button"
                        >
                          <i className="fas fa-trash me-2"></i>
                          Delete
                        </Button>
                      )}
                    </div>
                    <div>
                      <Button
                        variant="outline-secondary"
                        onClick={cancelEditing}
                        style={{ marginRight: "0.5rem" }}
                        type="button"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={saving}
                        style={{ padding: "0.375rem 1.5rem" }}
                      >
                        {saving ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              style={{ marginRight: "0.5rem" }}
                            />
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: "center" }}>
            <i
              className="fas fa-exclamation-triangle text-warning fa-3x mb-3"
              style={{ color: "#ffc107" }}
            ></i>
            <p>
              Are you sure you want to delete this contact information? This
              action cannot be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="fas fa-trash me-2"></i>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PersonalInfo;
